import type {RegistrationStep, RegistrationUrlData, ValidateStep} from './types';
import type {RegistrationStatus} from 'common/vendor-registration/types';

import {DownloadOutlined} from '@ant-design/icons';
import {Button} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {getRegistrationRoute} from 'common/vendor-registration/utils';
import {useURLFilter} from 'hooks/useURLFilter';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {downloadPdfSignedContract, getRegisterStatus} from 'store/slices/vendor-registration/thunk';

import BankInfo from './components/BankInfo/BankInfo';
import LoadingOverlay from './components/common/LoadingOverlay/LoadingOverlay';
import FaceRecognitionVerification from './components/FaceRecognitionVerification/FaceRecognitionVerification';
import NationalCard from './components/NationalCard/NationalCard';
import PersonalInfo from './components/PersonalInfo/PersonalInfo';
import RegisterCompleted from './components/RegisterCompleted/RegisterCompleted';
import RegistrationStepper from './components/RegistrationStepper/RegistrationStepper';
import UploadSignature from './components/UploadSignature/UploadSignature';
import VendorInfo from './components/VendorInfo/VendorInfo';
import {REGISTER_STEPS, VALID_REGISTRATION_STEP_VALUES} from './constants';
import {downloadBlob} from './utils';

const VendorRegister: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const validateStepRef = useRef<ValidateStep | null>(null);
  const defaultUrlData = useMemo<RegistrationUrlData>(() => ({}), []);
  const [, setUrlData] = useURLFilter<RegistrationUrlData>('data', defaultUrlData);
  const [currentStep, setCurrentStep] = useState<RegistrationStep | undefined>();
  const [isNationalCardUploaded, setIsNationalCardUploaded] = useState(false);
  const [isStatusResolved, setIsStatusResolved] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const {registerStatus} = useAppSelector((state) => state.vendorRegistration);

  const isKnownStep = useCallback(
    (step: RegistrationStatus['step']): step is RegistrationStep['value'] =>
      REGISTER_STEPS.some((registrationStep) => registrationStep.value === step),
    []
  );

  const selectStep = useCallback(
    (step?: RegistrationStep) => {
      if (!step) {
        return;
      }

      setUrlData(step);
      setCurrentStep(step);
    },
    [setUrlData]
  );

  const resolveStatus = useCallback(
    (status: RegistrationStatus) => {
      if (status.step === 'Register' && status.state === 'APPROVED') {
        selectStep(REGISTER_STEPS[0]);
        return;
      }

      if (status.step === 'KycIdentityInfo' && status.state === 'APPROVED') {
        selectStep(REGISTER_STEPS.find((step) => step.value === 'KycFaceRecognition'));
        return;
      }

      const targetRoute = getRegistrationRoute(status);

      if (targetRoute !== '/vendor-register') {
        navigate(targetRoute, {replace: true});
        return;
      }

      if (status.step === 'KycSendOtp') {
        selectStep(REGISTER_STEPS.find((step) => step.value === 'SignContract'));
        return;
      }

      if (!isKnownStep(status.step)) {
        navigate('/vendor-register-overview', {replace: true});
        return;
      }

      const statusStepIndex = REGISTER_STEPS.findIndex((step) => step.value === status.step);

      if (status.state === 'APPROVED') {
        selectStep(REGISTER_STEPS[statusStepIndex + 1] ?? REGISTER_STEPS[statusStepIndex]);
      } else if (['REJECTED', 'PENDING', 'DECLINED'].includes(status.state ?? '')) {
        selectStep(REGISTER_STEPS[statusStepIndex]);
      } else {
        navigate('/vendor-register-overview', {replace: true});
      }
    },
    [isKnownStep, navigate, selectStep]
  );

  const updateStatus = useCallback(async () => {
    try {
      const status = (await dispatch(getRegisterStatus()).unwrap()) as RegistrationStatus;
      resolveStatus(status);
    } catch {
      toast('خطا در دریافت وضعیت ثبت نام');
    }
  }, [dispatch, resolveStatus]);

  const handleNext = useCallback(
    async (hasOwnSubmit = false) => {
      if (isAdvancing) {
        return;
      }

      setIsAdvancing(true);

      try {
        const isValid = hasOwnSubmit ? true : await validateStepRef.current?.();

        if (
          isValid &&
          currentStep?.value &&
          VALID_REGISTRATION_STEP_VALUES.includes(currentStep.value)
        ) {
          await updateStatus();
        }
      } finally {
        setIsAdvancing(false);
      }
    },
    [currentStep?.value, isAdvancing, updateStatus]
  );

  const registerValidation = useCallback((validator: ValidateStep) => {
    validateStepRef.current = validator;
  }, []);

  const handleDownloadSignedContract = async () => {
    if (!registerStatus.trackingNumber) {
      toast('کد پیگیری قرارداد موجود نیست');
      return;
    }

    try {
      const blob = await dispatch(
        downloadPdfSignedContract({trackingNumber: registerStatus.trackingNumber})
      ).unwrap();
      downloadBlob(blob, `signed-contract-${registerStatus.trackingNumber}.pdf`);
    } catch {
      toast('خطا در دانلود قرارداد');
    }
  };

  useEffect(() => {
    if (currentStep?.value !== 'UploadDocuments') {
      setIsNationalCardUploaded(false);
    }
  }, [currentStep?.value]);

  useEffect(() => {
    let cancelled = false;

    void dispatch(getRegisterStatus())
      .unwrap()
      .then((status: RegistrationStatus) => {
        if (!cancelled) {
          resolveStatus(status);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast('خطا در دریافت وضعیت ثبت نام');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsStatusResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, resolveStatus]);

  if (!isStatusResolved || !currentStep) {
    return <LoadingOverlay loading />;
  }

  return (
    <PageLayout
      header={
        currentStep.value !== 'Finish' ? (
          <div>
            <div className="flex px-4 py-3">
              <h1 className="text-subtitle-md text-neutral-fg">{currentStep.title}</h1>
            </div>
            <RegistrationStepper
              currentStep={currentStep}
              selectedStepNumber={currentStep.no}
              steps={REGISTER_STEPS}
            />
          </div>
        ) : undefined
      }
      footer={
        currentStep.value === 'Finish' ? (
          <Button
            fullWidth
            onClick={handleDownloadSignedContract}
            startIcon={<DownloadOutlined aria-hidden />}
          >
            دانلود قرارداد نهایی
          </Button>
        ) : (
          <Button
            fullWidth
            onClick={() => void handleNext()}
            loading={isAdvancing}
            disabled={currentStep.value === 'UploadDocuments' && !isNationalCardUploaded}
          >
            مرحله بعد
          </Button>
        )
      }
    >
      {currentStep.value === 'GeneralInfo' ? (
        <PersonalInfo registerValidation={registerValidation} />
      ) : null}
      {currentStep.value === 'UploadDocuments' ? (
        <NationalCard
          registerValidation={registerValidation}
          onValidityChange={setIsNationalCardUploaded}
        />
      ) : null}
      {currentStep.value === 'VendorInfo' ? (
        <VendorInfo
          registerValidation={registerValidation}
          vendorName={registerStatus.vendorName}
        />
      ) : null}
      {currentStep.value === 'BankInfo' ? (
        <BankInfo registerValidation={registerValidation} />
      ) : null}
      {currentStep.value === 'KycFaceRecognition' ? (
        <FaceRecognitionVerification registerValidation={registerValidation} />
      ) : null}
      {currentStep.value === 'SignContract' ? (
        <UploadSignature
          registerValidation={registerValidation}
          onCompleted={() => void handleNext(true)}
        />
      ) : null}
      {currentStep.value === 'Finish' ? <RegisterCompleted /> : null}
    </PageLayout>
  );
};

export default VendorRegister;
